import { useState } from 'react';
import StarsDisplay from '@/pages/ratings/components/StarsDisplay';
import { laundryRatings } from '@/mocks/ratings';
import EmptyState from '@/components/base/EmptyState';

export default function LaundryRatingsTab() {
  const [filterStars, setFilterStars] = useState<number | null>(null);

  const totalRatings = laundryRatings.length;
  const averageStars =
    totalRatings > 0
      ? laundryRatings.reduce((acc, r) => acc + r.stars, 0) / totalRatings
      : 0;

  const starCounts = [5, 4, 3, 2, 1].map((star) => ({
    star,
    count: laundryRatings.filter((r) => r.stars === star).length,
    pct:
      totalRatings > 0
        ? Math.round((laundryRatings.filter((r) => r.stars === star).length / totalRatings) * 100)
        : 0,
  }));

  const filtered = filterStars
    ? laundryRatings.filter((r) => r.stars === filterStars)
    : laundryRatings;

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('ar-SA', { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div className="space-y-6">
      {/* Summary Card */}
      <div className="flex flex-col gap-5 rounded-card border border-background-200 bg-white p-5 shadow-card sm:flex-row sm:items-center">
        {/* Average */}
        <div className="flex flex-col items-center justify-center gap-1 border-b border-background-100 pb-4 text-center sm:border-b-0 sm:border-l sm:pb-0 sm:pl-6">
          <span className="text-[48px] font-bold leading-none text-foreground-950">
            {averageStars.toFixed(1)}
          </span>
          <StarsDisplay value={averageStars} size="md" />
          <span className="mt-1 text-[12px] text-foreground-400">{totalRatings} تقييم</span>
        </div>

        {/* Distribution bars */}
        <div className="flex-1 space-y-2">
          {starCounts.map(({ star, count, pct }) => (
            <button
              key={star}
              type="button"
              onClick={() => setFilterStars(filterStars === star ? null : star)}
              className={[
                'flex w-full items-center gap-3 rounded-lg px-2 py-1 transition-colors',
                filterStars === star ? 'bg-accent-50' : 'hover:bg-background-50',
              ].join(' ')}
            >
              <span className="flex w-12 shrink-0 items-center gap-0.5 text-[12px] text-foreground-500">
                {star}
                <i className="ri-star-fill text-[11px] text-accent-500" aria-hidden="true"></i>
              </span>
              <div className="flex-1 overflow-hidden rounded-full bg-background-100" style={{ height: '8px' }}>
                <div
                  className="h-full rounded-full bg-accent-500 transition-all duration-500"
                  style={{ width: `${pct}%` }}
                ></div>
              </div>
              <span className="w-8 shrink-0 text-left text-[12px] text-foreground-400">{count}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Filter info */}
      {filterStars && (
        <div className="flex items-center gap-2 text-[13px] text-foreground-500">
          <span>
            عرض تقييمات{' '}
            <span className="font-semibold text-accent-700">{filterStars} نجوم</span> فقط ({filtered.length} تقييم)
          </span>
          <button
            type="button"
            onClick={() => setFilterStars(null)}
            className="text-secondary-500 underline underline-offset-2 hover:text-secondary-700"
          >
            مسح الفلتر
          </button>
        </div>
      )}

      {/* Reviews List */}
      {filtered.length === 0 ? (
        <EmptyState
          icon="ri-star-line"
          title="لا توجد تقييمات بهذه الفئة"
          message="جرّب اختيار فئة نجوم أخرى أو امسح الفلتر."
        />
      ) : (
        <div className="space-y-3">
          {filtered.map((review) => (
            <div
              key={review.id}
              className="rounded-card border border-background-200 bg-white p-4 shadow-card"
            >
              <div className="flex items-start gap-3">
                {/* Avatar */}
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-[15px] font-bold text-secondary-700">
                  {review.clientInitial}
                </span>

                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-semibold text-[14px] text-foreground-950">
                      {review.clientName}
                    </span>
                    <div className="flex items-center gap-2">
                      <StarsDisplay value={review.stars} size="sm" />
                      <span className="text-[11px] text-foreground-400">
                        {formatDate(review.date)}
                      </span>
                    </div>
                  </div>

                  {review.comment && (
                    <p className="mt-2 text-[13px] leading-relaxed text-foreground-600">
                      {review.comment}
                    </p>
                  )}

                  <div className="mt-2 flex items-center gap-1 text-[11px] text-foreground-400">
                    <i className="ri-file-list-3-line text-[12px]" aria-hidden="true"></i>
                    <span>طلب {review.orderNo}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}